import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Loader2, Maximize2, Minimize2, Play, Square } from 'lucide-react'

import { useCameraLive } from '@/hooks/use-camera-live'
import { useCameraCircuitAutoStart } from '@/hooks/use-camera-circuit-auto-start'
import { useAuth } from '@/hooks/use-auth'
import { cameraLiveCircuit } from '@/lib/camera-live-circuit'
import { m } from '@/lib/paraglide/messages'

interface Props {
    vehicleId: string
    cameraId: number
    cameraName: string
    imageUrl?: string
    // `fill`: stretch to the parent's width and drop the expand/collapse
    // control. Used by the camera-wall cards, where the tile is one cell of a
    // fixed grid rather than a floating overlay chip.
    fill?: boolean
    // Kiosk/spotlight: tự phát live khi mount, không cần người bấm play. Chỉ
    // start một lần cho mỗi (vehicleId, cameraId).
    autoStart?: boolean
}
// Overlay controls sit on top of the video, so they cannot use <Button> (its
// default size-11 icon target would eat a third of a phone-width tile). They
// still have to be reachable: a 36px target on touch, a visible focus ring, and
// a real accessible name rather than a title tooltip alone.
const controlClass =
    'inline-flex size-9 items-center justify-center rounded bg-black/60 text-white transition-colors hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-black md:size-7'

function formatRetryTime(retryAt: number) {
    return new Date(retryAt).toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
    })
}

// One camera in the map overlay. Expand/collapse and live playback are local
// to this tile, so several tiles can stream at once.
export function FleetCameraTile({
    vehicleId,
    cameraId,
    cameraName,
    imageUrl,
    fill = false,
    autoStart = false,
}: Props) {
    const [expanded, setExpanded] = useState(true)
    const { hasPermission } = useAuth()
    const { state, error, videoRef, start, stop } = useCameraLive(
        vehicleId,
        cameraId,
    )
    const isLive = state === 'playing' || state === 'starting'
    const openCircuit =
        state === 'playing' ? null : cameraLiveCircuit.getOpenState(vehicleId)
    // kind=config (wrong IMEI, integration off) never heals by retrying —
    // getOpenState never schedules a retryAt for it, see camera-live-circuit.
    const isConfigError = openCircuit?.kind === 'config'
    const retryAt = openCircuit?.retryAt
    useCameraCircuitAutoStart({
        autoStart,
        vehicleId,
        cameraId,
        retryAt,
        start,
    })
    const startLabel = openCircuit
        ? m.fleet_camera_retry()
        : m.fleet_camera_live_start()

    // Collapsing counts as stopping: a stream the user cannot see should not
    // keep burning the vehicle's uplink.
    function handleCollapse() {
        void stop()
        setExpanded(false)
    }

    return (
        <div
            data-testid="camera-tile"
            data-expanded={expanded ? 'true' : 'false'}
            data-camera-id={cameraId}
            className={`relative overflow-hidden bg-black ${
                fill
                    ? 'w-full'
                    : `rounded-lg border border-white/20 shadow-lg transition-[width] duration-200 ease-out motion-reduce:transition-none ${
                          expanded ? 'w-40 sm:w-52 lg:w-64' : 'w-24 sm:w-28'
                      }`
            }`}
        >
            <div
                className="absolute right-1 top-1 z-10 flex gap-1"
                onClick={(e) => e.stopPropagation()}
            >
                {isLive ? (
                    <button
                        type="button"
                        data-testid="camera-live-stop"
                        aria-label={m.fleet_camera_live_stop()}
                        title={m.fleet_camera_live_stop()}
                        onClick={() => void stop()}
                        className={controlClass}
                    >
                        {state === 'starting' ? (
                            <Loader2 className="size-4 animate-spin motion-reduce:animate-none md:size-3.5" />
                        ) : (
                            <Square className="size-4 md:size-3.5" />
                        )}
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid="camera-live-start"
                        aria-label={startLabel}
                        title={startLabel}
                        onClick={() => void start()}
                        className={controlClass}
                    >
                        <Play className="size-4 md:size-3.5" />
                    </button>
                )}
                {!fill &&
                    (expanded ? (
                        <button
                            type="button"
                            data-testid="camera-collapse"
                            aria-label={m.fleet_camera_collapse()}
                            title={m.fleet_camera_collapse()}
                            onClick={handleCollapse}
                            className={controlClass}
                        >
                            <Minimize2 className="size-4 md:size-3.5" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            data-testid="camera-expand"
                            aria-label={m.fleet_camera_expand()}
                            title={m.fleet_camera_expand()}
                            onClick={() => setExpanded(true)}
                            className={controlClass}
                        >
                            <Maximize2 className="size-4 md:size-3.5" />
                        </button>
                    ))}
            </div>

            <div className="relative aspect-video w-full">
                <video
                    ref={videoRef}
                    playsInline
                    muted
                    className={`h-full w-full object-contain ${
                        state === 'playing' ? '' : 'hidden'
                    }`}
                />
                {state !== 'playing' &&
                    (imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={cameraName}
                            className="h-full w-full object-contain"
                        />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] leading-tight text-white/70">
                            {m.vehicle_camera_no_image()}
                        </div>
                    ))}
            </div>

            {/* The caption stays on when collapsed: it keeps the tile the same
                height in both states, so only the width animates instead of the
                box snapping vertically. It also keeps the channel identifiable
                once the picture is too small to read.
                The tile is always black regardless of theme, so the error colour
                is a fixed light red rather than --destructive, which is tuned
                for the page background and goes muddy here. */}
            {openCircuit && isConfigError ? (
                <div
                    className="space-y-0.5 px-2 py-1 text-[11px] leading-tight text-white/70"
                    title={openCircuit.lastError}
                >
                    <p>{m.fleet_camera_config_error()}</p>
                    {hasPermission('vehicles.update') && (
                        <Link
                            to="/vehicles/$vehicleId"
                            params={{ vehicleId }}
                            className="underline"
                        >
                            {m.fleet_camera_edit_vehicle()}
                        </Link>
                    )}
                </div>
            ) : openCircuit ? (
                <div
                    className="space-y-0.5 px-2 py-1 text-[11px] leading-tight text-red-300"
                    title={openCircuit.lastError}
                >
                    <p>
                        {m.fleet_camera_circuit_open({
                            time: formatRetryTime(openCircuit.retryAt ?? 0),
                        })}
                    </p>
                    <p className="break-words text-[10px]">
                        {openCircuit.lastError}
                    </p>
                </div>
            ) : (
                <p
                    className={`truncate px-2 py-1 text-[11px] ${
                        error ? 'text-red-300' : 'text-white/90'
                    }`}
                    title={error || cameraName}
                >
                    {error ? error : cameraName}
                </p>
            )}
        </div>
    )
}
